import { Coordinates, GeolocationError } from '../types/location';

export type GetLocationResult = {
  success: true;
  coordinates: Coordinates;
} | {
  success: false;
  error: GeolocationError;
}

export async function getCurrentLocation(
  options?: PositionOptions
): Promise<GetLocationResult> {
  return new Promise((resolve) => {
    // Check if geolocation is supported
    if (!navigator.geolocation) {
      resolve({
        success: false,
        error: {
          code: 0, // Custom code for unsupported
          message: 'Geolocation is not supported by this browser'
        }
      });
      return;
    }

    const defaultOptions: PositionOptions = {
      enableHighAccuracy: true,
      timeout: 10000,
      maximumAge: 300000, // 5 minutes cache
      ...options
    };

    navigator.geolocation.getCurrentPosition(
      (position) => {
        resolve({
          success: true,
          coordinates: {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude
          }
        });
      },
      (error) => {
        let errorMessage: string;
        
        switch (error.code) {
          case error.PERMISSION_DENIED:
            errorMessage = 'Location access denied by user';
            break;
          case error.POSITION_UNAVAILABLE:
            errorMessage = 'Location information unavailable';
            break;
          case error.TIMEOUT:
            errorMessage = 'Location request timed out';
            break;
          default:
            errorMessage = 'An unknown error occurred while retrieving location';
        }

        resolve({
          success: false,
          error: {
            code: error.code,
            message: errorMessage
          }
        });
      },
      defaultOptions
    );
  });
}

export async function checkLocationPermission(): Promise<'granted' | 'denied' | 'prompt' | 'unavailable'> {
  if (!navigator.permissions || !navigator.geolocation) {
    return 'unavailable';
  }

  try {
    const permission = await navigator.permissions.query({ name: 'geolocation' });
    return permission.state as 'granted' | 'denied' | 'prompt';
  } catch (error) {
    // Fallback for browsers that don't support permissions query
    console.warn('Could not query geolocation permission:', error);
    return 'prompt';
  }
}

export function watchLocation(
  onLocationUpdate: (coordinates: Coordinates) => void,
  onError: (error: GeolocationError) => void,
  options?: PositionOptions
): number | null {
  if (!navigator.geolocation) {
    onError({
      code: 0,
      message: 'Geolocation is not supported by this browser'
    });
    return null;
  }

  const defaultOptions: PositionOptions = {
    enableHighAccuracy: true,
    timeout: 10000,
    maximumAge: 60000, // 1 minute cache for watching
    ...options
  };

  return navigator.geolocation.watchPosition(
    (position) => {
      onLocationUpdate({
        latitude: position.coords.latitude,
        longitude: position.coords.longitude
      });
    },
    (error) => {
      let errorMessage: string;
      
      switch (error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location access denied by user';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information unavailable';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out';
          break;
        default:
          errorMessage = 'An unknown error occurred while watching location';
      }

      onError({
        code: error.code,
        message: errorMessage
      });
    },
    defaultOptions
  );
}

export function stopWatchingLocation(watchId: number): void {
  if (navigator.geolocation) {
    navigator.geolocation.clearWatch(watchId);
  }
}